import React from 'react';
import { Ticket, useTickets } from './useTickets';
import { useReleases, Release } from './useReleases';

export interface ReleaseWithTickets extends Omit<Release, 'tickets'> {
  tickets: (string | Ticket)[];
}

export function useReleasesWithTickets() {
  const { tickets, status: ticketsStatus, error: ticketsError } = useTickets();
  const { releases, status: releasesStatus, error: releasesError } = useReleases();

  const keyTicketMap = React.useMemo(() => new Map(tickets.map((t) => [t.jiraKey, t])), [tickets]);

  const releasesWithTickets = React.useMemo<ReleaseWithTickets[]>(() => {
    return releases.map<ReleaseWithTickets>((v) => {
      const tickets = [...v.tickets].sort((a, b) => {
        const [aProject, aId] = a.split('-');
        const [bProject, bId] = b.split('-');
        return aProject.localeCompare(bProject) || Number(aId) - Number(bId);
      });

      return {
        ...v,
        tickets: tickets.map((key) => keyTicketMap.get(key) ?? key),
      };
    });
  }, [keyTicketMap, releases]);

  return {
    releasesWithTickets,
    status:
      ticketsStatus === 'error' || releasesStatus === 'error'
        ? ('error' as const)
        : ticketsStatus === 'loading' || releasesStatus === 'loading'
        ? ('loading' as const)
        : ('success' as const),
    error:
      ticketsError || releasesError ? new Error(`Failed to fetch releases: ${ticketsError?.message} ${releasesError?.message}`.trim()) : undefined,
  };
}
